/**
 * ISO 3166-1 countries — the one country table every surface reads (web
 * picker, client view, X-ray widget, PDF blocks, party validation).
 *
 * `code` is alpha-2 (`NO`), which is what the platform stores on a client, a
 * client party and a Client Country rule. `alpha3` (`NOR`) is what Sumsub and
 * Morningstar report; alpha-3 → alpha-2 comparison (identity checks,
 * Morningstar exposure) reads the same rows.
 *
 * QL-17 — `names` is a fixed table, not `Intl.DisplayNames`, so the browser
 * and the PDF renderer print the same string. nb_NO was generated once from
 * ICU and hand-corrected; other locales fall back to en_GB.
 */

import type { LocaleCode } from "../i18n";

export type CountryNames = { en_GB: string; nb_NO: string };

export type Country = {
  /** ISO 3166-1 alpha-2, e.g. `NO`. */
  code: string;
  /** ISO 3166-1 alpha-3, e.g. `NOR`. */
  alpha3: string;
  names: CountryNames;
};

export const COUNTRIES: readonly Country[] = [
  { code: "AF", alpha3: "AFG", names: { en_GB: "Afghanistan", nb_NO: "Afghanistan" } },
  { code: "AX", alpha3: "ALA", names: { en_GB: "Åland Islands", nb_NO: "Åland" } },
  { code: "AL", alpha3: "ALB", names: { en_GB: "Albania", nb_NO: "Albania" } },
  { code: "DZ", alpha3: "DZA", names: { en_GB: "Algeria", nb_NO: "Algerie" } },
  { code: "AS", alpha3: "ASM", names: { en_GB: "American Samoa", nb_NO: "Amerikansk Samoa" } },
  { code: "AD", alpha3: "AND", names: { en_GB: "Andorra", nb_NO: "Andorra" } },
  { code: "AO", alpha3: "AGO", names: { en_GB: "Angola", nb_NO: "Angola" } },
  { code: "AI", alpha3: "AIA", names: { en_GB: "Anguilla", nb_NO: "Anguilla" } },
  { code: "AQ", alpha3: "ATA", names: { en_GB: "Antarctica", nb_NO: "Antarktis" } },
  {
    code: "AG",
    alpha3: "ATG",
    names: { en_GB: "Antigua and Barbuda", nb_NO: "Antigua og Barbuda" },
  },
  { code: "AR", alpha3: "ARG", names: { en_GB: "Argentina", nb_NO: "Argentina" } },
  { code: "AM", alpha3: "ARM", names: { en_GB: "Armenia", nb_NO: "Armenia" } },
  { code: "AW", alpha3: "ABW", names: { en_GB: "Aruba", nb_NO: "Aruba" } },
  { code: "AU", alpha3: "AUS", names: { en_GB: "Australia", nb_NO: "Australia" } },
  { code: "AT", alpha3: "AUT", names: { en_GB: "Austria", nb_NO: "Østerrike" } },
  { code: "AZ", alpha3: "AZE", names: { en_GB: "Azerbaijan", nb_NO: "Aserbajdsjan" } },
  { code: "BS", alpha3: "BHS", names: { en_GB: "Bahamas", nb_NO: "Bahamas" } },
  { code: "BH", alpha3: "BHR", names: { en_GB: "Bahrain", nb_NO: "Bahrain" } },
  { code: "BD", alpha3: "BGD", names: { en_GB: "Bangladesh", nb_NO: "Bangladesh" } },
  { code: "BB", alpha3: "BRB", names: { en_GB: "Barbados", nb_NO: "Barbados" } },
  { code: "BY", alpha3: "BLR", names: { en_GB: "Belarus", nb_NO: "Belarus" } },
  { code: "BE", alpha3: "BEL", names: { en_GB: "Belgium", nb_NO: "Belgia" } },
  { code: "BZ", alpha3: "BLZ", names: { en_GB: "Belize", nb_NO: "Belize" } },
  { code: "BJ", alpha3: "BEN", names: { en_GB: "Benin", nb_NO: "Benin" } },
  { code: "BM", alpha3: "BMU", names: { en_GB: "Bermuda", nb_NO: "Bermuda" } },
  { code: "BT", alpha3: "BTN", names: { en_GB: "Bhutan", nb_NO: "Bhutan" } },
  { code: "BO", alpha3: "BOL", names: { en_GB: "Bolivia", nb_NO: "Bolivia" } },
  {
    code: "BQ",
    alpha3: "BES",
    names: { en_GB: "Bonaire, Sint Eustatius and Saba", nb_NO: "Karibisk Nederland" },
  },
  {
    code: "BA",
    alpha3: "BIH",
    names: { en_GB: "Bosnia and Herzegovina", nb_NO: "Bosnia-Hercegovina" },
  },
  { code: "BW", alpha3: "BWA", names: { en_GB: "Botswana", nb_NO: "Botswana" } },
  { code: "BV", alpha3: "BVT", names: { en_GB: "Bouvet Island", nb_NO: "Bouvetøya" } },
  { code: "BR", alpha3: "BRA", names: { en_GB: "Brazil", nb_NO: "Brasil" } },
  {
    code: "IO",
    alpha3: "IOT",
    names: {
      en_GB: "British Indian Ocean Territory",
      nb_NO: "Det britiske territoriet i Indiahavet",
    },
  },
  { code: "BN", alpha3: "BRN", names: { en_GB: "Brunei Darussalam", nb_NO: "Brunei" } },
  { code: "BG", alpha3: "BGR", names: { en_GB: "Bulgaria", nb_NO: "Bulgaria" } },
  { code: "BF", alpha3: "BFA", names: { en_GB: "Burkina Faso", nb_NO: "Burkina Faso" } },
  { code: "BI", alpha3: "BDI", names: { en_GB: "Burundi", nb_NO: "Burundi" } },
  { code: "CV", alpha3: "CPV", names: { en_GB: "Cabo Verde", nb_NO: "Kapp Verde" } },
  { code: "KH", alpha3: "KHM", names: { en_GB: "Cambodia", nb_NO: "Kambodsja" } },
  { code: "CM", alpha3: "CMR", names: { en_GB: "Cameroon", nb_NO: "Kamerun" } },
  { code: "CA", alpha3: "CAN", names: { en_GB: "Canada", nb_NO: "Canada" } },
  { code: "KY", alpha3: "CYM", names: { en_GB: "Cayman Islands", nb_NO: "Caymanøyene" } },
  {
    code: "CF",
    alpha3: "CAF",
    names: { en_GB: "Central African Republic", nb_NO: "Den sentralafrikanske republikk" },
  },
  { code: "TD", alpha3: "TCD", names: { en_GB: "Chad", nb_NO: "Tsjad" } },
  { code: "CL", alpha3: "CHL", names: { en_GB: "Chile", nb_NO: "Chile" } },
  { code: "CN", alpha3: "CHN", names: { en_GB: "China", nb_NO: "Kina" } },
  { code: "CX", alpha3: "CXR", names: { en_GB: "Christmas Island", nb_NO: "Christmasøya" } },
  { code: "CC", alpha3: "CCK", names: { en_GB: "Cocos (Keeling) Islands", nb_NO: "Kokosøyene" } },
  { code: "CO", alpha3: "COL", names: { en_GB: "Colombia", nb_NO: "Colombia" } },
  { code: "KM", alpha3: "COM", names: { en_GB: "Comoros", nb_NO: "Komorene" } },
  { code: "CG", alpha3: "COG", names: { en_GB: "Congo", nb_NO: "Kongo-Brazzaville" } },
  {
    code: "CD",
    alpha3: "COD",
    names: { en_GB: "Congo (Democratic Republic)", nb_NO: "Kongo-Kinshasa" },
  },
  { code: "CK", alpha3: "COK", names: { en_GB: "Cook Islands", nb_NO: "Cookøyene" } },
  { code: "CR", alpha3: "CRI", names: { en_GB: "Costa Rica", nb_NO: "Costa Rica" } },
  { code: "CI", alpha3: "CIV", names: { en_GB: "Côte d'Ivoire", nb_NO: "Elfenbenskysten" } },
  { code: "HR", alpha3: "HRV", names: { en_GB: "Croatia", nb_NO: "Kroatia" } },
  { code: "CU", alpha3: "CUB", names: { en_GB: "Cuba", nb_NO: "Cuba" } },
  { code: "CW", alpha3: "CUW", names: { en_GB: "Curaçao", nb_NO: "Curaçao" } },
  { code: "CY", alpha3: "CYP", names: { en_GB: "Cyprus", nb_NO: "Kypros" } },
  { code: "CZ", alpha3: "CZE", names: { en_GB: "Czech Republic", nb_NO: "Tsjekkia" } },
  { code: "DK", alpha3: "DNK", names: { en_GB: "Denmark", nb_NO: "Danmark" } },
  { code: "DJ", alpha3: "DJI", names: { en_GB: "Djibouti", nb_NO: "Djibouti" } },
  { code: "DM", alpha3: "DMA", names: { en_GB: "Dominica", nb_NO: "Dominica" } },
  {
    code: "DO",
    alpha3: "DOM",
    names: { en_GB: "Dominican Republic", nb_NO: "Den dominikanske republikk" },
  },
  { code: "EC", alpha3: "ECU", names: { en_GB: "Ecuador", nb_NO: "Ecuador" } },
  { code: "EG", alpha3: "EGY", names: { en_GB: "Egypt", nb_NO: "Egypt" } },
  { code: "SV", alpha3: "SLV", names: { en_GB: "El Salvador", nb_NO: "El Salvador" } },
  { code: "GQ", alpha3: "GNQ", names: { en_GB: "Equatorial Guinea", nb_NO: "Ekvatorial-Guinea" } },
  { code: "ER", alpha3: "ERI", names: { en_GB: "Eritrea", nb_NO: "Eritrea" } },
  { code: "EE", alpha3: "EST", names: { en_GB: "Estonia", nb_NO: "Estland" } },
  { code: "SZ", alpha3: "SWZ", names: { en_GB: "Eswatini", nb_NO: "Eswatini" } },
  { code: "ET", alpha3: "ETH", names: { en_GB: "Ethiopia", nb_NO: "Etiopia" } },
  { code: "FK", alpha3: "FLK", names: { en_GB: "Falkland Islands", nb_NO: "Falklandsøyene" } },
  { code: "FO", alpha3: "FRO", names: { en_GB: "Faroe Islands", nb_NO: "Færøyene" } },
  { code: "FJ", alpha3: "FJI", names: { en_GB: "Fiji", nb_NO: "Fiji" } },
  { code: "FI", alpha3: "FIN", names: { en_GB: "Finland", nb_NO: "Finland" } },
  { code: "FR", alpha3: "FRA", names: { en_GB: "France", nb_NO: "Frankrike" } },
  { code: "GF", alpha3: "GUF", names: { en_GB: "French Guiana", nb_NO: "Fransk Guyana" } },
  { code: "PF", alpha3: "PYF", names: { en_GB: "French Polynesia", nb_NO: "Fransk Polynesia" } },
  {
    code: "TF",
    alpha3: "ATF",
    names: { en_GB: "French Southern Territories", nb_NO: "De franske sørterritorier" },
  },
  { code: "GA", alpha3: "GAB", names: { en_GB: "Gabon", nb_NO: "Gabon" } },
  { code: "GM", alpha3: "GMB", names: { en_GB: "Gambia", nb_NO: "Gambia" } },
  { code: "GE", alpha3: "GEO", names: { en_GB: "Georgia", nb_NO: "Georgia" } },
  { code: "DE", alpha3: "DEU", names: { en_GB: "Germany", nb_NO: "Tyskland" } },
  { code: "GH", alpha3: "GHA", names: { en_GB: "Ghana", nb_NO: "Ghana" } },
  { code: "GI", alpha3: "GIB", names: { en_GB: "Gibraltar", nb_NO: "Gibraltar" } },
  { code: "GR", alpha3: "GRC", names: { en_GB: "Greece", nb_NO: "Hellas" } },
  { code: "GL", alpha3: "GRL", names: { en_GB: "Greenland", nb_NO: "Grønland" } },
  { code: "GD", alpha3: "GRD", names: { en_GB: "Grenada", nb_NO: "Grenada" } },
  { code: "GP", alpha3: "GLP", names: { en_GB: "Guadeloupe", nb_NO: "Guadeloupe" } },
  { code: "GU", alpha3: "GUM", names: { en_GB: "Guam", nb_NO: "Guam" } },
  { code: "GT", alpha3: "GTM", names: { en_GB: "Guatemala", nb_NO: "Guatemala" } },
  { code: "GG", alpha3: "GGY", names: { en_GB: "Guernsey", nb_NO: "Guernsey" } },
  { code: "GN", alpha3: "GIN", names: { en_GB: "Guinea", nb_NO: "Guinea" } },
  { code: "GW", alpha3: "GNB", names: { en_GB: "Guinea-Bissau", nb_NO: "Guinea-Bissau" } },
  { code: "GY", alpha3: "GUY", names: { en_GB: "Guyana", nb_NO: "Guyana" } },
  { code: "HT", alpha3: "HTI", names: { en_GB: "Haiti", nb_NO: "Haiti" } },
  {
    code: "HM",
    alpha3: "HMD",
    names: { en_GB: "Heard Island and McDonald Islands", nb_NO: "Heard- og McDonaldøyene" },
  },
  { code: "VA", alpha3: "VAT", names: { en_GB: "Holy See", nb_NO: "Vatikanstaten" } },
  { code: "HN", alpha3: "HND", names: { en_GB: "Honduras", nb_NO: "Honduras" } },
  { code: "HK", alpha3: "HKG", names: { en_GB: "Hong Kong", nb_NO: "Hong Kong" } },
  { code: "HU", alpha3: "HUN", names: { en_GB: "Hungary", nb_NO: "Ungarn" } },
  { code: "IS", alpha3: "ISL", names: { en_GB: "Iceland", nb_NO: "Island" } },
  { code: "IN", alpha3: "IND", names: { en_GB: "India", nb_NO: "India" } },
  { code: "ID", alpha3: "IDN", names: { en_GB: "Indonesia", nb_NO: "Indonesia" } },
  { code: "IR", alpha3: "IRN", names: { en_GB: "Iran", nb_NO: "Iran" } },
  { code: "IQ", alpha3: "IRQ", names: { en_GB: "Iraq", nb_NO: "Irak" } },
  { code: "IE", alpha3: "IRL", names: { en_GB: "Ireland", nb_NO: "Irland" } },
  { code: "IM", alpha3: "IMN", names: { en_GB: "Isle of Man", nb_NO: "Man" } },
  { code: "IL", alpha3: "ISR", names: { en_GB: "Israel", nb_NO: "Israel" } },
  { code: "IT", alpha3: "ITA", names: { en_GB: "Italy", nb_NO: "Italia" } },
  { code: "JM", alpha3: "JAM", names: { en_GB: "Jamaica", nb_NO: "Jamaica" } },
  { code: "JP", alpha3: "JPN", names: { en_GB: "Japan", nb_NO: "Japan" } },
  { code: "JE", alpha3: "JEY", names: { en_GB: "Jersey", nb_NO: "Jersey" } },
  { code: "JO", alpha3: "JOR", names: { en_GB: "Jordan", nb_NO: "Jordan" } },
  { code: "KZ", alpha3: "KAZ", names: { en_GB: "Kazakhstan", nb_NO: "Kasakhstan" } },
  { code: "KE", alpha3: "KEN", names: { en_GB: "Kenya", nb_NO: "Kenya" } },
  { code: "KI", alpha3: "KIR", names: { en_GB: "Kiribati", nb_NO: "Kiribati" } },
  { code: "KP", alpha3: "PRK", names: { en_GB: "Korea (North)", nb_NO: "Nord-Korea" } },
  { code: "KR", alpha3: "KOR", names: { en_GB: "South Korea", nb_NO: "Sør-Korea" } },
  { code: "KW", alpha3: "KWT", names: { en_GB: "Kuwait", nb_NO: "Kuwait" } },
  { code: "KG", alpha3: "KGZ", names: { en_GB: "Kyrgyzstan", nb_NO: "Kirgisistan" } },
  {
    code: "LA",
    alpha3: "LAO",
    names: { en_GB: "Lao People's Democratic Republic", nb_NO: "Laos" },
  },
  { code: "LV", alpha3: "LVA", names: { en_GB: "Latvia", nb_NO: "Latvia" } },
  { code: "LB", alpha3: "LBN", names: { en_GB: "Lebanon", nb_NO: "Libanon" } },
  { code: "LS", alpha3: "LSO", names: { en_GB: "Lesotho", nb_NO: "Lesotho" } },
  { code: "LR", alpha3: "LBR", names: { en_GB: "Liberia", nb_NO: "Liberia" } },
  { code: "LY", alpha3: "LBY", names: { en_GB: "Libya", nb_NO: "Libya" } },
  { code: "LI", alpha3: "LIE", names: { en_GB: "Liechtenstein", nb_NO: "Liechtenstein" } },
  { code: "LT", alpha3: "LTU", names: { en_GB: "Lithuania", nb_NO: "Litauen" } },
  { code: "LU", alpha3: "LUX", names: { en_GB: "Luxembourg", nb_NO: "Luxemburg" } },
  { code: "MO", alpha3: "MAC", names: { en_GB: "Macao", nb_NO: "Macao" } },
  { code: "MG", alpha3: "MDG", names: { en_GB: "Madagascar", nb_NO: "Madagaskar" } },
  { code: "MW", alpha3: "MWI", names: { en_GB: "Malawi", nb_NO: "Malawi" } },
  { code: "MY", alpha3: "MYS", names: { en_GB: "Malaysia", nb_NO: "Malaysia" } },
  { code: "MV", alpha3: "MDV", names: { en_GB: "Maldives", nb_NO: "Maldivene" } },
  { code: "ML", alpha3: "MLI", names: { en_GB: "Mali", nb_NO: "Mali" } },
  { code: "MT", alpha3: "MLT", names: { en_GB: "Malta", nb_NO: "Malta" } },
  { code: "MH", alpha3: "MHL", names: { en_GB: "Marshall Islands", nb_NO: "Marshalløyene" } },
  { code: "MQ", alpha3: "MTQ", names: { en_GB: "Martinique", nb_NO: "Martinique" } },
  { code: "MR", alpha3: "MRT", names: { en_GB: "Mauritania", nb_NO: "Mauritania" } },
  { code: "MU", alpha3: "MUS", names: { en_GB: "Mauritius", nb_NO: "Mauritius" } },
  { code: "YT", alpha3: "MYT", names: { en_GB: "Mayotte", nb_NO: "Mayotte" } },
  { code: "MX", alpha3: "MEX", names: { en_GB: "Mexico", nb_NO: "Mexico" } },
  { code: "FM", alpha3: "FSM", names: { en_GB: "Micronesia", nb_NO: "Mikronesiaføderasjonen" } },
  { code: "MD", alpha3: "MDA", names: { en_GB: "Moldova", nb_NO: "Moldova" } },
  { code: "MC", alpha3: "MCO", names: { en_GB: "Monaco", nb_NO: "Monaco" } },
  { code: "MN", alpha3: "MNG", names: { en_GB: "Mongolia", nb_NO: "Mongolia" } },
  { code: "ME", alpha3: "MNE", names: { en_GB: "Montenegro", nb_NO: "Montenegro" } },
  { code: "MS", alpha3: "MSR", names: { en_GB: "Montserrat", nb_NO: "Montserrat" } },
  { code: "MA", alpha3: "MAR", names: { en_GB: "Morocco", nb_NO: "Marokko" } },
  { code: "MZ", alpha3: "MOZ", names: { en_GB: "Mozambique", nb_NO: "Mosambik" } },
  { code: "MM", alpha3: "MMR", names: { en_GB: "Myanmar", nb_NO: "Myanmar" } },
  { code: "NA", alpha3: "NAM", names: { en_GB: "Namibia", nb_NO: "Namibia" } },
  { code: "NR", alpha3: "NRU", names: { en_GB: "Nauru", nb_NO: "Nauru" } },
  { code: "NP", alpha3: "NPL", names: { en_GB: "Nepal", nb_NO: "Nepal" } },
  { code: "NL", alpha3: "NLD", names: { en_GB: "Netherlands", nb_NO: "Nederland" } },
  { code: "NC", alpha3: "NCL", names: { en_GB: "New Caledonia", nb_NO: "Ny-Caledonia" } },
  { code: "NZ", alpha3: "NZL", names: { en_GB: "New Zealand", nb_NO: "New Zealand" } },
  { code: "NI", alpha3: "NIC", names: { en_GB: "Nicaragua", nb_NO: "Nicaragua" } },
  { code: "NE", alpha3: "NER", names: { en_GB: "Niger", nb_NO: "Niger" } },
  { code: "NG", alpha3: "NGA", names: { en_GB: "Nigeria", nb_NO: "Nigeria" } },
  { code: "NU", alpha3: "NIU", names: { en_GB: "Niue", nb_NO: "Niue" } },
  { code: "NF", alpha3: "NFK", names: { en_GB: "Norfolk Island", nb_NO: "Norfolkøya" } },
  { code: "MK", alpha3: "MKD", names: { en_GB: "North Macedonia", nb_NO: "Nord-Makedonia" } },
  {
    code: "MP",
    alpha3: "MNP",
    names: { en_GB: "Northern Mariana Islands", nb_NO: "Nord-Marianene" },
  },
  { code: "NO", alpha3: "NOR", names: { en_GB: "Norway", nb_NO: "Norge" } },
  { code: "OM", alpha3: "OMN", names: { en_GB: "Oman", nb_NO: "Oman" } },
  { code: "PK", alpha3: "PAK", names: { en_GB: "Pakistan", nb_NO: "Pakistan" } },
  { code: "PW", alpha3: "PLW", names: { en_GB: "Palau", nb_NO: "Palau" } },
  { code: "PS", alpha3: "PSE", names: { en_GB: "Palestine", nb_NO: "Palestina" } },
  { code: "PA", alpha3: "PAN", names: { en_GB: "Panama", nb_NO: "Panama" } },
  { code: "PG", alpha3: "PNG", names: { en_GB: "Papua New Guinea", nb_NO: "Papua Ny-Guinea" } },
  { code: "PY", alpha3: "PRY", names: { en_GB: "Paraguay", nb_NO: "Paraguay" } },
  { code: "PE", alpha3: "PER", names: { en_GB: "Peru", nb_NO: "Peru" } },
  { code: "PH", alpha3: "PHL", names: { en_GB: "Philippines", nb_NO: "Filippinene" } },
  { code: "PN", alpha3: "PCN", names: { en_GB: "Pitcairn", nb_NO: "Pitcairnøyene" } },
  { code: "PL", alpha3: "POL", names: { en_GB: "Poland", nb_NO: "Polen" } },
  { code: "PT", alpha3: "PRT", names: { en_GB: "Portugal", nb_NO: "Portugal" } },
  { code: "PR", alpha3: "PRI", names: { en_GB: "Puerto Rico", nb_NO: "Puerto Rico" } },
  { code: "QA", alpha3: "QAT", names: { en_GB: "Qatar", nb_NO: "Qatar" } },
  { code: "RE", alpha3: "REU", names: { en_GB: "Réunion", nb_NO: "Réunion" } },
  { code: "RO", alpha3: "ROU", names: { en_GB: "Romania", nb_NO: "Romania" } },
  { code: "RU", alpha3: "RUS", names: { en_GB: "Russia", nb_NO: "Russland" } },
  { code: "RW", alpha3: "RWA", names: { en_GB: "Rwanda", nb_NO: "Rwanda" } },
  { code: "BL", alpha3: "BLM", names: { en_GB: "Saint Barthélemy", nb_NO: "Saint-Barthélemy" } },
  { code: "SH", alpha3: "SHN", names: { en_GB: "Saint Helena", nb_NO: "St. Helena" } },
  {
    code: "KN",
    alpha3: "KNA",
    names: { en_GB: "Saint Kitts and Nevis", nb_NO: "Saint Kitts og Nevis" },
  },
  { code: "LC", alpha3: "LCA", names: { en_GB: "Saint Lucia", nb_NO: "St. Lucia" } },
  { code: "MF", alpha3: "MAF", names: { en_GB: "Saint Martin", nb_NO: "Saint-Martin" } },
  {
    code: "PM",
    alpha3: "SPM",
    names: { en_GB: "Saint Pierre and Miquelon", nb_NO: "Saint-Pierre-et-Miquelon" },
  },
  {
    code: "VC",
    alpha3: "VCT",
    names: { en_GB: "Saint Vincent and the Grenadines", nb_NO: "St. Vincent og Grenadinene" },
  },
  { code: "WS", alpha3: "WSM", names: { en_GB: "Samoa", nb_NO: "Samoa" } },
  { code: "SM", alpha3: "SMR", names: { en_GB: "San Marino", nb_NO: "San Marino" } },
  {
    code: "ST",
    alpha3: "STP",
    names: { en_GB: "Sao Tome and Principe", nb_NO: "São Tomé og Príncipe" },
  },
  { code: "SA", alpha3: "SAU", names: { en_GB: "Saudi Arabia", nb_NO: "Saudi-Arabia" } },
  { code: "SN", alpha3: "SEN", names: { en_GB: "Senegal", nb_NO: "Senegal" } },
  { code: "RS", alpha3: "SRB", names: { en_GB: "Serbia", nb_NO: "Serbia" } },
  { code: "SC", alpha3: "SYC", names: { en_GB: "Seychelles", nb_NO: "Seychellene" } },
  { code: "SL", alpha3: "SLE", names: { en_GB: "Sierra Leone", nb_NO: "Sierra Leone" } },
  { code: "SG", alpha3: "SGP", names: { en_GB: "Singapore", nb_NO: "Singapore" } },
  { code: "SX", alpha3: "SXM", names: { en_GB: "Sint Maarten", nb_NO: "Sint Maarten" } },
  { code: "SK", alpha3: "SVK", names: { en_GB: "Slovakia", nb_NO: "Slovakia" } },
  { code: "SI", alpha3: "SVN", names: { en_GB: "Slovenia", nb_NO: "Slovenia" } },
  { code: "SB", alpha3: "SLB", names: { en_GB: "Solomon Islands", nb_NO: "Salomonøyene" } },
  { code: "SO", alpha3: "SOM", names: { en_GB: "Somalia", nb_NO: "Somalia" } },
  { code: "ZA", alpha3: "ZAF", names: { en_GB: "South Africa", nb_NO: "Sør-Afrika" } },
  {
    code: "GS",
    alpha3: "SGS",
    names: { en_GB: "South Georgia", nb_NO: "Sør-Georgia og Sør-Sandwichøyene" },
  },
  { code: "SS", alpha3: "SSD", names: { en_GB: "South Sudan", nb_NO: "Sør-Sudan" } },
  { code: "ES", alpha3: "ESP", names: { en_GB: "Spain", nb_NO: "Spania" } },
  { code: "LK", alpha3: "LKA", names: { en_GB: "Sri Lanka", nb_NO: "Sri Lanka" } },
  { code: "SD", alpha3: "SDN", names: { en_GB: "Sudan", nb_NO: "Sudan" } },
  { code: "SR", alpha3: "SUR", names: { en_GB: "Suriname", nb_NO: "Surinam" } },
  {
    code: "SJ",
    alpha3: "SJM",
    names: { en_GB: "Svalbard and Jan Mayen", nb_NO: "Svalbard og Jan Mayen" },
  },
  { code: "SE", alpha3: "SWE", names: { en_GB: "Sweden", nb_NO: "Sverige" } },
  { code: "CH", alpha3: "CHE", names: { en_GB: "Switzerland", nb_NO: "Sveits" } },
  { code: "SY", alpha3: "SYR", names: { en_GB: "Syrian Arab Republic", nb_NO: "Syria" } },
  { code: "TW", alpha3: "TWN", names: { en_GB: "Taiwan", nb_NO: "Taiwan" } },
  { code: "TJ", alpha3: "TJK", names: { en_GB: "Tajikistan", nb_NO: "Tadsjikistan" } },
  { code: "TZ", alpha3: "TZA", names: { en_GB: "Tanzania", nb_NO: "Tanzania" } },
  { code: "TH", alpha3: "THA", names: { en_GB: "Thailand", nb_NO: "Thailand" } },
  { code: "TL", alpha3: "TLS", names: { en_GB: "Timor-Leste", nb_NO: "Øst-Timor" } },
  { code: "TG", alpha3: "TGO", names: { en_GB: "Togo", nb_NO: "Togo" } },
  { code: "TK", alpha3: "TKL", names: { en_GB: "Tokelau", nb_NO: "Tokelau" } },
  { code: "TO", alpha3: "TON", names: { en_GB: "Tonga", nb_NO: "Tonga" } },
  {
    code: "TT",
    alpha3: "TTO",
    names: { en_GB: "Trinidad and Tobago", nb_NO: "Trinidad og Tobago" },
  },
  { code: "TN", alpha3: "TUN", names: { en_GB: "Tunisia", nb_NO: "Tunisia" } },
  { code: "TR", alpha3: "TUR", names: { en_GB: "Turkey", nb_NO: "Tyrkia" } },
  { code: "TM", alpha3: "TKM", names: { en_GB: "Turkmenistan", nb_NO: "Turkmenistan" } },
  {
    code: "TC",
    alpha3: "TCA",
    names: { en_GB: "Turks and Caicos Islands", nb_NO: "Turks- og Caicosøyene" },
  },
  { code: "TV", alpha3: "TUV", names: { en_GB: "Tuvalu", nb_NO: "Tuvalu" } },
  { code: "UG", alpha3: "UGA", names: { en_GB: "Uganda", nb_NO: "Uganda" } },
  { code: "UA", alpha3: "UKR", names: { en_GB: "Ukraine", nb_NO: "Ukraina" } },
  {
    code: "AE",
    alpha3: "ARE",
    names: { en_GB: "United Arab Emirates", nb_NO: "De forente arabiske emirater" },
  },
  { code: "GB", alpha3: "GBR", names: { en_GB: "United Kingdom", nb_NO: "Storbritannia" } },
  { code: "US", alpha3: "USA", names: { en_GB: "United States", nb_NO: "USA" } },
  {
    code: "UM",
    alpha3: "UMI",
    names: { en_GB: "United States Minor Outlying Islands", nb_NO: "USAs ytre øyer" },
  },
  { code: "UY", alpha3: "URY", names: { en_GB: "Uruguay", nb_NO: "Uruguay" } },
  { code: "UZ", alpha3: "UZB", names: { en_GB: "Uzbekistan", nb_NO: "Usbekistan" } },
  { code: "VU", alpha3: "VUT", names: { en_GB: "Vanuatu", nb_NO: "Vanuatu" } },
  { code: "VE", alpha3: "VEN", names: { en_GB: "Venezuela", nb_NO: "Venezuela" } },
  { code: "VN", alpha3: "VNM", names: { en_GB: "Vietnam", nb_NO: "Vietnam" } },
  {
    code: "VG",
    alpha3: "VGB",
    names: { en_GB: "Virgin Islands (British)", nb_NO: "De britiske jomfruøyene" },
  },
  {
    code: "VI",
    alpha3: "VIR",
    names: { en_GB: "Virgin Islands (U.S.)", nb_NO: "De amerikanske jomfruøyene" },
  },
  { code: "WF", alpha3: "WLF", names: { en_GB: "Wallis and Futuna", nb_NO: "Wallis og Futuna" } },
  { code: "EH", alpha3: "ESH", names: { en_GB: "Western Sahara", nb_NO: "Vest-Sahara" } },
  { code: "YE", alpha3: "YEM", names: { en_GB: "Yemen", nb_NO: "Jemen" } },
  { code: "ZM", alpha3: "ZMB", names: { en_GB: "Zambia", nb_NO: "Zambia" } },
  { code: "ZW", alpha3: "ZWE", names: { en_GB: "Zimbabwe", nb_NO: "Zimbabwe" } },
];

/** alpha-3 → alpha-2 for every ISO 3166-1 country. */
export const ALPHA3_TO_ALPHA2: Readonly<Record<string, string>> = Object.fromEntries(
  COUNTRIES.map((c) => [c.alpha3, c.code]),
);

/** alpha-2 → alpha-3 for every ISO 3166-1 country. */
export const ALPHA2_TO_ALPHA3: Readonly<Record<string, string>> = Object.fromEntries(
  COUNTRIES.map((c) => [c.code, c.alpha3]),
);

/**
 * Reduce a country to alpha-2 for comparison, or null when it cannot be read.
 * Accepts alpha-2 unchanged (trimmed, upper-cased) and alpha-3 via the table.
 * An unrecognised code is "cannot compare", never a mismatch — see
 * `identityMismatches`.
 */
export function toAlpha2(country: string | null | undefined): string | null {
  const raw = country?.trim().toUpperCase();
  if (!raw) return null;
  if (raw.length === 2) return raw;
  return ALPHA3_TO_ALPHA2[raw] ?? null;
}

/**
 * Reduce a country to alpha-3, or null when it cannot be read. Accepts alpha-3
 * unchanged (trimmed, upper-cased) and alpha-2 via the table.
 */
export function toAlpha3(country: string | null | undefined): string | null {
  const raw = country?.trim().toUpperCase();
  if (!raw) return null;
  if (raw.length === 3) return raw;
  return ALPHA2_TO_ALPHA3[raw] ?? null;
}

const BY_ALPHA2: ReadonlyMap<string, Country> = new Map(COUNTRIES.map((c) => [c.code, c]));

/** True for an assigned ISO 3166-1 alpha-2 code (case-insensitive). */
export function isKnownAlpha2Code(input: string): boolean {
  return BY_ALPHA2.has(input.toUpperCase());
}

/**
 * Morningstar's "Not Classified" bucket — exposure not attributable to any
 * single country. Not a country: the picker and validation never offer it.
 * "99" still occurs in analysis snapshots saved before the parser mapped it.
 */
export const MISC_COUNTRY_CODE = "OTHER";
const MISC_NAMES: CountryNames = { en_GB: "Miscellaneous", nb_NO: "Diverse" };
const MISC_FLAG = "🇺🇳";

function isMiscCountryCode(code: string): boolean {
  return code === MISC_COUNTRY_CODE || code === "99";
}

/** Lower-cased name (either language) or common alias → alpha-2. */
const BY_NAME: ReadonlyMap<string, string> = new Map([
  ...COUNTRIES.flatMap((c) => [
    [c.names.en_GB.toLowerCase(), c.code] as const,
    [c.names.nb_NO.toLowerCase(), c.code] as const,
  ]),
  ["usa", "US"],
  ["uk", "GB"],
  ["britain", "GB"],
  ["great britain", "GB"],
  ["korea", "KR"],
  ["korea (south)", "KR"],
  ["russian federation", "RU"],
  ["viet nam", "VN"],
]);

/**
 * Alpha-2 for a country given as alpha-2, alpha-3 or a name (English or
 * Norwegian, case-insensitive), else null. Lenient on purpose: Morningstar
 * holdings carry whatever the vendor sends.
 */
export function resolveCountryCode(input: string | null | undefined): string | null {
  const raw = input?.trim();
  if (!raw) return null;
  const upper = raw.toUpperCase();
  if (BY_ALPHA2.has(upper)) return upper;
  const fromAlpha3 = ALPHA3_TO_ALPHA2[upper];
  if (fromAlpha3) return fromAlpha3;
  return BY_NAME.get(raw.toLowerCase()) ?? null;
}

/**
 * Display name of a country in `lang` (en_GB when the table has no such
 * language). Accepts anything `resolveCountryCode` does plus the misc
 * bucket; an unreadable code comes back as given.
 */
export function getCountryName(code: string, lang: LocaleCode | string): string {
  const names = isMiscCountryCode(code)
    ? MISC_NAMES
    : BY_ALPHA2.get(resolveCountryCode(code) ?? "")?.names;
  if (!names) return code;
  return names[lang as keyof CountryNames] ?? names.en_GB;
}

/** Flag emoji for a country (regional indicator symbols), or "". */
export function getCountryFlag(code: string): string {
  if (isMiscCountryCode(code)) return MISC_FLAG;
  const alpha2 = resolveCountryCode(code);
  if (!alpha2) return "";
  return String.fromCodePoint(...[...alpha2].map((ch) => 127397 + ch.charCodeAt(0)));
}

/** Flag emoji + localized name, e.g. "🇳🇴 Norge". */
export function getCountryNameWithFlag(code: string, lang: LocaleCode | string): string {
  const flag = getCountryFlag(code);
  const name = getCountryName(code, lang);
  return flag ? `${flag} ${name}` : name;
}
